import { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { VStack, Spinner, Text, Banner, Button } from 'wss3-forge'

type SsoState = 'processing' | 'success' | 'error'

interface SsoCallbackState {
  app_id?: string
  redirect_uri?: string
  connection_id?: string
  protocol?: 'saml' | 'oidc'
}

export function SsoCallback() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [status, setStatus] = useState<SsoState>('processing')
  const [errorMessage, setErrorMessage] = useState('')

  useEffect(() => {
    handleCallback()
  }, [])

  const handleCallback = async () => {
    try {
      // Get the SSO state from sessionStorage
      const stateJson = sessionStorage.getItem('sso_state')
      const ssoState: SsoCallbackState = stateJson ? JSON.parse(stateJson) : {}

      // Check if this is an OIDC callback (has code parameter)
      const code = searchParams.get('code')
      const state = searchParams.get('state')
      const error = searchParams.get('error')
      const errorDescription = searchParams.get('error_description')

      if (error) {
        throw new Error(errorDescription || error)
      }

      if (code) {
        // OIDC callback
        await handleOidcCallback(code, state, ssoState)
      } else {
        // Check for SAML POST response
        // SAML responses come as POST, which won't be available in React
        // We need to handle this via an edge function that processes the POST
        // and redirects back with a session token

        // For now, check if we have a token in the URL (from edge function redirect)
        const token = searchParams.get('token')
        const sessionToken = searchParams.get('session')

        if (token || sessionToken) {
          await handleSamlCallback(token || sessionToken!, ssoState)
        } else {
          throw new Error('Invalid SSO callback. No authorization code or token provided.')
        }
      }
    } catch (err) {
      console.error('SSO callback error:', err)
      setStatus('error')
      setErrorMessage(err instanceof Error ? err.message : 'Unknown error')
    }
  }

  const handleOidcCallback = async (code: string, state: string | null, ssoState: SsoCallbackState) => {
    // Verify state matches what we stored
    const storedState = sessionStorage.getItem('sso_oidc_state')
    if (state && storedState && state !== storedState) {
      throw new Error('Invalid state parameter. Possible CSRF attack.')
    }

    // Exchange code for tokens via edge function
    const { data, error } = await supabase.functions.invoke('sso-oidc-callback', {
      body: {
        code,
        connection_id: ssoState.connection_id,
        redirect_uri: window.location.origin + '/auth/sso/callback'
      }
    })

    if (error) throw error

    if (!data.success) {
      throw new Error(data.error || 'OIDC authentication failed')
    }

    // Clear SSO state
    sessionStorage.removeItem('sso_state')
    sessionStorage.removeItem('sso_oidc_state')

    // Handle successful authentication
    await handleAuthSuccess(data, ssoState)
  }

  const handleSamlCallback = async (token: string, ssoState: SsoCallbackState) => {
    // The token here is a session token created by the edge function
    // after validating the SAML assertion

    // Set the session
    const { data: { session }, error } = await supabase.auth.setSession({
      access_token: token,
      refresh_token: token // Will be replaced by actual refresh token
    })

    if (error) throw error

    // Clear SSO state
    sessionStorage.removeItem('sso_state')

    // Handle successful authentication
    await handleAuthSuccess({ user_id: session?.user?.id }, ssoState)
  }

  const handleAuthSuccess = async (data: { user_id?: string; auth_code?: string }, ssoState: SsoCallbackState) => {
    setStatus('success')

    // If there's an app redirect
    if (ssoState.app_id && ssoState.redirect_uri) {
      // Generate auth code for the app
      const { data: codeData, error: codeError } = await supabase.rpc('generate_auth_code', {
        p_user_id: data.user_id,
        p_app_id: ssoState.app_id,
        p_redirect_uri: ssoState.redirect_uri
      })

      if (codeError) {
        console.error('Failed to generate auth code:', codeError)
        // Fall back to home
        setTimeout(() => navigate('/'), 1500)
        return
      }

      // Redirect to app with code
      const redirectUrl = new URL(ssoState.redirect_uri)
      redirectUrl.searchParams.set('code', codeData)
      window.location.href = redirectUrl.toString()
    } else {
      // Redirect to home
      setTimeout(() => navigate('/'), 1500)
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'var(--bg-primary)',
      padding: '2rem'
    }}>
      <VStack gap="lg" style={{ textAlign: 'center', maxWidth: '400px' }}>
        {status === 'processing' && (
          <>
            <Spinner size="lg" />
            <Text>Authentification en cours...</Text>
            <Text size="sm" color="muted">
              Veuillez patienter pendant que nous vérifions vos identifiants.
            </Text>
          </>
        )}

        {status === 'success' && (
          <>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              backgroundColor: 'var(--color-success-bg)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto'
            }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                <path d="M20 6L9 17L4 12" stroke="var(--color-success)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <Text weight="medium">Connexion réussie !</Text>
            <Text size="sm" color="muted">
              Vous allez être redirigé...
            </Text>
          </>
        )}

        {status === 'error' && (
          <>
            <Banner variant="error" title="Échec de l'authentification">
              {errorMessage}
            </Banner>
            <Button onClick={() => navigate('/login')}>
              Retour à la connexion
            </Button>
          </>
        )}
      </VStack>
    </div>
  )
}
